import { useCallback, useEffect, useState } from "react";

import { visitas as traerVisitas } from "../services/visitas";
import {
  AVISO,
  aQuien,
  estadoAviso,
  hora,
  pendientes,
  porDia,
} from "../shared/visitas";
import Loading from "./Loading";

/* Quién entró caminando al portón, y a quién venía a ver.
 *
 * ── ES DE MIRAR, Y ESO NO ES UNA LIMITACIÓN ───────────────────────────────
 *
 * Se cargan por WhatsApp desde el portón, con la persona parada adelante:
 * «Ingreso: Carlos, empresa Michelin, entra caminando, viene a ver a Nicolás
 * Cantarini». Un formulario acá sería un segundo lugar para lo mismo, y el día
 * que se cargue en el que no es esa visita no aparece en ningún lado. Es la
 * lección de los dos talleres.
 *
 * ── LAS QUE NO SE PUDIERON AVISAR VAN ARRIBA Y EN SU PROPIO BLOQUE ────────
 *
 * No es un orden: es un bloque aparte con su cuenta. Mezcladas con las veinte
 * que sí se avisaron, las dos que fallaron hay que ir a buscarlas — y una
 * lista de control que hay que filtrar para usar no se usa. Es lo que hace la
 * home con lo que vence y el pañol con lo que no llegó.
 *
 * ── PERO «NO ESTÁ EN LA LISTA» NO ES UNA FALLA ───────────────────────────
 *
 * Igna lo pidió con esas palabras: *«si el número no está en la lista que no
 * le llegue nada pero que quede registrado»*. O sea que es el comportamiento
 * correcto, no un problema — y por eso NO entra en el bloque de arriba ni en
 * el contador. Contarlo ahí convertiría lo normal en un pendiente y a la
 * semana nadie miraría el número.
 *
 * ── Y EL PERMISO ES EL DE PORTERÍA, SIN INVENTAR UNA LLAVE NUEVA ─────────
 *
 * Esto es el registro del portón y vive en su universo: quien puede abrir
 * «Ingreso y egreso» puede abrir esto. Un permiso propio sería una llave que
 * no abre nada distinto — y una que hay que acordarse de dar es una que un día
 * no se da, y ahí la pantalla «no anda» sin que nada lo explique.
 */

const Pastilla = ({ v }) => {
  const e = estadoAviso(v);
  const { texto, tono } = AVISO[e];
  return (
    <span className={`pill ${tono === "ok" ? "ok" : tono === "mal" ? "mal" : ""}`}>
      {texto}
    </span>
  );
};

const Fila = ({ v }) => (
  <tr className={v.anulado_en ? "tenue" : undefined}>
    <td className="tenue">{hora(v.ingreso_en)}</td>
    <td>
      <b>{v.nombre}</b>
      {/* DE DÓNDE VENÍA, que es la tercera cosa que se pidió guardar. Sin
          empresa no se dibuja un hueco: mucha gente entra sin representar a
          ninguna, y un «sin empresa» en cada fila convertiría lo normal en un
          dato que falta. */}
      {v.empresa && <span className="sub">{v.empresa}</span>}
    </td>
    <td>
      {aQuien(v) || <span className="sin-dato">no se dijo</span>}
      {/* EL NOMBRE QUE SE ESCRIBIÓ, cuando difiere del que resolvió. Es lo
          que permite entender por qué un aviso salió para «Nicolás Cantarini»
          cuando en la puerta dijeron «Nico». */}
      {v.a_quien_nombre && v.a_quien && v.a_quien !== v.a_quien_nombre && (
        <span className="sub">se escribió «{v.a_quien}»</span>
      )}
    </td>
    <td>{v.como || <span className="sin-dato">—</span>}</td>
    <td>
      <Pastilla v={v} />
      {/* POR QUÉ NO SALIÓ. «No se pudo avisar» a secas manda a revisar el
          teléfono de esa persona; el motivo dice si fue Meta o el número. */}
      {estadoAviso(v) === "fallo" && v.aviso_motivo && (
        <span className="sub">{v.aviso_motivo}</span>
      )}
    </td>
    <td className="tenue">{v.quien || ""}</td>
  </tr>
);

const Tabla = ({ filas }) => (
  <div className="tabla-wrap">
    <table className="tabla fija">
      <thead>
        <tr>
          <th>Hora</th>
          <th>Quién entró</th>
          <th>Viene a ver a</th>
          <th>Cómo</th>
          <th>Aviso</th>
          <th>Cargó</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((v) => (
          <Fila key={v.id} v={v} />
        ))}
      </tbody>
    </table>
  </div>
);

/* El rango que se pide. Abre en HOY porque a esta pantalla se entra a ver
 * quién está adentro ahora — al revés que el parte del puerto, que cierra la
 * semana anterior porque es un informe. */
const DIAS = [
  { id: "hoy", label: "Hoy", dias: 1 },
  { id: "semana", label: "7 días", dias: 7 },
  { id: "mes", label: "30 días", dias: 30 },
];

function rango(dias) {
  const ahora = new Date();
  const hasta = new Date(ahora.getTime() + 24 * 3600 * 1000);
  // El corte va en UTC−3, que es donde vive la jornada. Con el día del
  // navegador, alguien mirando desde otro huso vería «hoy» empezando a otra
  // hora que la que marca el reloj del portón.
  const desde = new Date(ahora.getTime() - (dias - 1) * 24 * 3600 * 1000);
  desde.setUTCHours(3, 0, 0, 0);
  return [desde.toISOString(), hasta.toISOString()];
}

const Visitas = () => {
  const [cuanto, setCuanto] = useState("hoy");
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);

  const traer = useCallback(async () => {
    const d = DIAS.find((x) => x.id === cuanto) || DIAS[0];
    setFilas(null);
    setError(null);
    try {
      const [desde, hasta] = rango(d.dias);
      setFilas(await traerVisitas(desde, hasta));
    } catch (e) {
      setError(e.message || "No se pudieron leer las visitas");
    }
  }, [cuanto]);

  useEffect(() => {
    traer();
  }, [traer]);

  /* «NO PUDE LEER» NO SE DICE COMO «NO VINO NADIE». Sobre una pantalla vacía,
   * el de portería concluiría que no entró nadie en todo el día — que es
   * exactamente lo contrario de lo que pasó. Misma regla que las pestañas de
   * la Previsión. */
  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <Loading />;

  const fallaron = filas.filter((v) => estadoAviso(v) === "fallo");
  const resto = filas.filter((v) => estadoAviso(v) !== "fallo");
  const n = pendientes(filas);

  return (
    <div className="docs">
      <div className="tk-filtros">
        {DIAS.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`tk-chip${cuanto === d.id ? " activo" : ""}`}
            onClick={() => setCuanto(d.id)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {error && <p className="panel-aviso suave">{error}</p>}

      {/* LO QUE HAY QUE MIRAR, ARRIBA Y APARTE. Con su cuenta: sin el número,
          un bloque plegado y uno vacío se ven igual. */}
      {n > 0 && (
        <>
          <h3 className="docs-sub">
            Sin avisar · {n}
          </h3>
          <p className="panel-aviso suave">
            A estas personas les avisamos que la visita llegó y{" "}
            <b>el mensaje no se entregó</b>. Hay que llamarlas: alguien está
            esperando en recepción.
          </p>
          <Tabla filas={fallaron} />
        </>
      )}

      {filas.length === 0 && (
        <p className="panel-aviso suave">
          No hay visitas anotadas en este período.
        </p>
      )}

      {resto.length > 0 && (
        <>
          {n > 0 && <h3 className="docs-sub">El resto</h3>}
          {porDia(resto).map(({ dia: d, visitas: vs }) => (
            <div key={d}>
              {porDia(resto).length > 1 && <h3 className="docs-sub">{d}</h3>}
              <Tabla filas={vs} />
            </div>
          ))}
        </>
      )}

      <p className="docs-pie">
        Se cargan por WhatsApp: el mensaje tiene que decir{" "}
        <b>«viene a ver a»</b>.
      </p>
    </div>
  );
};

export default Visitas;
